/**
 * External task system form component — CoordioCore form pattern.
 * Uses PageFormLayout with left sidebar navigation and FormSection cards.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, Link } from 'lucide-react';

import {
  CoordioDropdown,
  FormField,
  Input,
  Switch,
  Textarea,
} from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { hitlService } from '../../services/hitlService';
import { useNotificationStore } from '../../store/notificationStore';

const SYSTEM_TYPES = ['ServiceNow', 'Jira', 'Azure DevOps', 'Custom'];

/**
 * External task system form component for creating and editing external task systems.
 */
export const ExternalTaskSystemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemType, setSystemType] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [authenticationJson, setAuthenticationJson] = useState('');
  const [taskMappingJson, setTaskMappingJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await hitlService.getExternalTaskSystem(id);
      setName(data.name);
      setDescription(data.description || '');
      setSystemType(data.systemType);
      setApiBaseUrl(data.apiBaseUrl);
      setAuthenticationJson('');
      setTaskMappingJson('');
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load external task system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!systemType.trim()) {
      setError('System type is required');
      return;
    }

    if (!apiBaseUrl.trim()) {
      setError('API Base URL is required');
      return;
    }

    try {
      new URL(apiBaseUrl.trim());
    } catch {
      setError('API Base URL must be a valid URL');
      return;
    }

    if (authenticationJson.trim()) {
      try {
        JSON.parse(authenticationJson.trim());
      } catch {
        setError('Authentication JSON must be valid JSON');
        return;
      }
    }

    if (taskMappingJson.trim()) {
      try {
        JSON.parse(taskMappingJson.trim());
      } catch {
        setError('Task mapping JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        systemType: systemType.trim(),
        apiBaseUrl: apiBaseUrl.trim(),
        authenticationJson: authenticationJson.trim() || undefined,
        taskMappingJson: taskMappingJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        addNotification('Update functionality not yet implemented', 'info');
      } else {
        await hitlService.createExternalTaskSystem(data);
        addNotification('External task system created successfully', 'success');
      }
      navigate('/external-task-systems');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save external task system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit External Task System' : 'Create External Task System'}
      subtitle="Connect an external ticketing system for human-in-the-loop task routing."
      data-testid="external-task-system-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/external-task-systems' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/external-task-systems'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <ClipboardList size={16} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'connection', label: 'Connection', icon: <Link size={16} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and type of this external task system.',
          icon: <ClipboardList size={20} />,
          content: (
            <FormSection
              title="System identity"
              description="Choose the external platform type and give this integration a descriptive name."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Unique identifier for this external task system."
                  data-testid="external-task-system-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="external-task-system-name"
                  />
                </FormField>

                <FormField
                  label="System Type"
                  required
                  description="The external platform this integration connects to."
                  data-testid="external-task-system-systemType"
                >
                  <CoordioDropdown
                    options={SYSTEM_TYPES.map((type) => ({ label: type, value: type }))}
                    value={systemType || null}
                    onChange={(v) => setSystemType(v ?? '')}
                    clearable={false}
                    data-testid="external-task-system-systemType"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Optional context about this integration."
                  data-testid="external-task-system-description"
                >
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="external-task-system-description"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'connection',
          title: 'Connection',
          description: 'Endpoint URL, authentication credentials, field mapping, and status.',
          icon: <Link size={20} />,
          content: (
            <FormSection
              title="Connection settings"
              description="Configure the API endpoint, authentication, and field mapping for this integration."
            >
              <FormField
                label="API Base URL"
                required
                description="The base URL of the external task system API (e.g., https://your-instance.service-now.com/api)."
                data-testid="external-task-system-apiBaseUrl"
              >
                <Input
                  className="w-full"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder="https://your-instance.service-now.com/api"
                  data-testid="external-task-system-apiBaseUrl"
                />
              </FormField>

              <div className="mt-5">
                <FormField
                  label="Authentication JSON"
                  description="JSON configuration for authentication (API keys, OAuth tokens, etc.)."
                  data-testid="external-task-system-authenticationJson"
                >
                  <Textarea
                    className="w-full font-mono text-[13px]"
                    value={authenticationJson}
                    onChange={(e) => setAuthenticationJson(e.target.value)}
                    rows={6}
                    placeholder={'{ "apiKey": "your-key", "username": "user" }'}
                    data-testid="external-task-system-authenticationJson"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Task Mapping JSON"
                  description="JSON configuration for mapping internal fields to external system fields."
                  data-testid="external-task-system-taskMappingJson"
                >
                  <Textarea
                    className="w-full font-mono text-[13px]"
                    value={taskMappingJson}
                    onChange={(e) => setTaskMappingJson(e.target.value)}
                    rows={6}
                    placeholder={'{ "title": "summary", "description": "description", "status": "state" }'}
                    data-testid="external-task-system-taskMappingJson"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        data-testid="external-task-system-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
